import { Injectable } from '@angular/core';

import { BetState } from '@bpos/v1/my-bets';
import { Fixture, FixtureStage, FixtureType } from '@cds/betting-offer';
import { PicksView } from '@cds/betting-offer/domain-specific';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { emptyOdds } from '@frontend/sports/odds/feature';
import { SyncUserSettingsService, UserService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';
import {
    IMyBetsBetWithOptionMarketInfo,
    IMyBetsBetWithResultInfo,
    MyBetsBetSlip,
} from 'packages/sports/web/app/src/my-bets-base/models/my-bets-viewmodels';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2StandardPick } from '../../../core/picks/betslip-v2-standard-pick';
import { CombinationPrevention } from '../../../core/picks/combo-prevention';
import { MinimumCombination } from '../../../core/picks/minimum-combination';
import { V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import { PriceType, ResultStatus } from '../../../core/picks/pick-models';
import { EditMyBetPicksPayload } from '../../../model/edit-mybet/edit-bet-init';
import BetslipPickFactory from '../../picks/factory/betslip-pick.factory';
import { IFillEditMyBetPayload } from '../actions';

/**
 * Converts the my bets data to edit bet data, so we can fill the edit bet.
 */
@Injectable()
export class EditBetConvertersService {
    constructor(
        private user: UserService,
        private userSettings: SyncUserSettingsService,
        protected pickFactory: BetslipPickFactory,
    ) {}

    editMyBetPickToFilledMyBet(payload: EditMyBetPicksPayload): IFillEditMyBetPayload {
        const result: IFillEditMyBetPayload = {
            pickState: {},
            sourceBetslip: payload.sourceBetslip,
        };
        const defaultEditBetPickState = {
            isRemoved: false,
            canSwap: true,
            isHighlighted: false,
        };

        const picks = Array.from(this.getPicks(payload.picksView, payload.sourceBetslip));

        for (let i = 0; i < picks.length; i++) {
            const pick = picks[i];
            result.pickState[i] = {
                pick,
                state: {
                    ...defaultEditBetPickState,
                    resultedState: pick.resultStatus,
                },
            };
        }

        if (result.sourceBetslip.earlyPayout) {
            result.sourceBetslip.earlyPayout.value = this.roundEarlyPayout(result);
        }

        return result;
    }

    protected convertBetStateToResultedState(state: BetState): ResultStatus {
        switch (state) {
            case BetState.Canceled:
                return ResultStatus.Canceled;
            case BetState.Lost:
                return ResultStatus.Lost;
            case BetState.Open:
                return ResultStatus.Open;
            case BetState.Placed:
                return ResultStatus.Won;
            case BetState.WithHeld:
                return ResultStatus.Lost;
            case BetState.Won:
                return ResultStatus.Won;
            default:
                throw new Error('Unknown bet state!');
        }
    }

    protected *getPicks(response: PicksView, betslip: MyBetsBetSlip): Iterable<BetslipPick> {
        const bets = betslip.bets.sort((first, second) => {
            return first.betIndex - second.betIndex;
        });
        for (const bet of bets) {
            if (bet.resultInfo) {
                yield this.createV1Pick(response, bet as IMyBetsBetWithResultInfo);
            }
            if (bet.optionMarketInfo) {
                yield this.createV2OptionMarketPick(response, bet as IMyBetsBetWithOptionMarketInfo);
            }
        }
    }

    protected createV1Pick(response: PicksView, bet: IMyBetsBetWithResultInfo): BetslipV1Pick {
        const liveFallback = response.liveFallbacks[bet.resultInfo.optionId];
        const fixtureId = liveFallback ? liveFallback.fixtureId : bet.resultInfo.eventId.toString();
        const marketId = liveFallback ? liveFallback.gameId : bet.resultInfo.marketId;
        const optionId = liveFallback ? liveFallback.resultId : bet.resultInfo.optionId;

        const fixture = response.fixturePage.fixtures.find((f) => f.id === fixtureId);
        const optionAvailable = this.optionAvailable(fixture, marketId, optionId);

        if (optionAvailable) {
            const pickId = new V1PickId(fixtureId, marketId, optionId);

            const v1Pick = this.pickFactory.createPick(pickId, { source: 'mybets' }, fixture!) as Nullable<BetslipV1Pick>;
            if (v1Pick) {
                v1Pick.resultStatus = this.convertBetStateToResultedState(bet.state);

                return v1Pick;
            }
        }

        return this.createV1PickFromViewModel(bet);
    }

    protected createV2OptionMarketPick(response: PicksView, bet: IMyBetsBetWithOptionMarketInfo): BetslipV2OptionMarketPick {
        const fixture = response.fixturePage.fixtures.find((f) => f.id === bet.optionMarketInfo.fixtureCompoundId);
        if (fixture) {
            const pickId = new V2OptionMarketPickId(
                bet.optionMarketInfo.fixtureCompoundId,
                bet.optionMarketInfo.optionMarketId,
                bet.optionMarketInfo.optionId,
                bet.optionMarketInfo.priceId,
            );
            const optionPick = this.pickFactory.createPick(pickId, { source: 'mybets' }, fixture) as BetslipV2OptionMarketPick;
            if (optionPick) {
                optionPick.resultStatus = this.convertBetStateToResultedState(bet.state);

                return optionPick;
            }
        }

        return this.createV2OptionMarketPickFromViewModel(bet);
    }

    /**
     * We need to round the early payout value in Greece and to show it without tax in Germany.
     *
     * @param payload
     */
    private roundEarlyPayout(payload: IFillEditMyBetPayload): number {
        let earlyPayoutValue = new Decimal(payload.sourceBetslip.earlyPayout!.value || 0);
        if (earlyPayoutValue.greaterThan(0)) {
            if (this.userSettings.configSettings.winningsTaxRate > 0) {
                // Germany show value without tax
                const undoTaxRatio = new Decimal(1).minus(this.userSettings.configSettings.winningsTaxRate);
                // Round to the second digit. First removes everything after 3rd.
                earlyPayoutValue = earlyPayoutValue.dividedBy(undoTaxRatio).toDecimalPlaces(2, Decimal.ROUND_UP);
            }
            if (this.user.columnSize > 0) {
                // Greece round to the lowers columns size.
                earlyPayoutValue = earlyPayoutValue.toNearest(this.user.columnSize, Decimal.ROUND_DOWN);
            }
        }

        return earlyPayoutValue.toNumber();
    }

    private optionAvailable(fixture: Fixture | undefined, marketId: number, optionId: number): boolean {
        return (
            !!fixture &&
            (fixture.hybridFixtureData != null
                ? fixture.optionMarkets.some((g) => g.id === marketId && g.options.some((r) => r.id === optionId))
                : fixture.games.some((g) => g.id === marketId && g.results.some((r) => r.id === optionId)))
        );
    }

    private createV1PickFromViewModel(vm: IMyBetsBetWithResultInfo): BetslipV1Pick {
        // open date will be in the past, cut of date will be in the past.

        const cutOffDate = new Date();
        const info = vm.resultInfo;

        const pick = new BetslipV1Pick();
        pick.resultStatus = this.convertBetStateToResultedState(vm.state);
        pick.id = new V1PickId(info.eventId.toString(), info.marketId, info.optionId);

        pick.sport = {
            id: vm.sportId,
            name: {
                name: vm.sportName,
                signature: '',
            },
        };

        pick.league = {
            id: info.leagueId,
            parentLeagueId: info.leagueId,
            name: {
                name: vm.leagueName,
                signature: '',
            },
        };

        pick.region = {
            id: 0,
            name: {
                name: '',
                signature: '',
            },
        };

        pick.event = {
            id: info.eventId.toString(),
            name: {
                name: vm.eventName,
                signature: '',
            },
            eventDate: vm.eventDate,
            cutOffDate,
            isLive: false,
            isPublished: false,
            groupId: null,
        };

        pick.market = {
            id: info.marketId,
            name: {
                name: info.marketName,
                signature: '',
            },
            comboPrevention: CombinationPrevention.AllCombo,
            minimumCombo: MinimumCombination.Single,
            isVisible: false,
            isClosed: true,
            templateId: 0,
            isBetBuilderEnabled: false,
        };
        pick.option = {
            id: info.optionId,
            name: {
                name: info.optionName,
                signature: '',
            },
            price: {
                id: info.optionId,
                type: PriceType.Fixed,
                isVisible: false,
                nativeOdds: {
                    decimals: vm.odds.european ?? 0,
                    fractional: {
                        numerator: vm.odds.british?.numerator,
                        denominator: vm.odds.british?.denominator,
                    },
                    moneyline: vm.odds.american ?? 0,
                },
                marketId: info.marketId,
            },
        };

        pick.acceptedPrice = {
            price: { ...pick.option.price },
            isManuallyAccepted: false,
        };

        return pick;
    }

    private createV2OptionMarketPickFromViewModel(vm: IMyBetsBetWithOptionMarketInfo): BetslipV2OptionMarketPick {
        const info = vm.optionMarketInfo;
        const pick = new BetslipV2StandardPick();
        pick.id = new V2OptionMarketPickId(info.fixtureCompoundId, info.optionMarketId, info.optionId, info.priceId);
        pick.resultStatus = this.convertBetStateToResultedState(vm.state);
        pick.fixture = {
            cutOffDate: new Date(),
            fixtureType: FixtureType.Standard,
            fixtureId: info.fixtureCompoundId,
            competitionGroupId: null,
            name: {
                name: vm.eventName,
                signature: '',
            },
            eventDate: vm.eventDate,
            region: {
                id: vm.leagueId,
                name: {
                    name: vm.leagueName,
                    signature: '',
                },
            },
            league: { id: vm.leagueId, name: { name: vm.leagueName, signature: '' } },
            sportId: 0,
            sportName: {
                name: '',
                signature: '',
            },
            stage: FixtureStage.Live,
            virtual: false,
            linkedEventIds: [],
        };

        pick.market = {
            id: info.optionMarketId,
            name: {
                name: vm.marketName,
                signature: '',
            },
            isEachWay: false,
            isVisible: false,
            isClosed: true,
            comboPrevention: CombinationPrevention.NoFixtureCombo,
            minimumCombo: 1,
            placeTerms: null,
            isBetBuilderEnabled: false,
        };

        pick.option = {
            id: info.optionId,
            name: {
                name: vm.optionName,
                signature: '',
            },
            isVisible: false,
            prices: [
                {
                    id: info.optionId,
                    isVisible: false,
                    marketId: info.optionMarketId,
                    type: PriceType.Fixed,
                    nativeOdds: emptyOdds,
                },
            ],
        };

        pick.acceptedPrice = {
            price: { ...pick.option.prices[0] },
            isManuallyAccepted: false,
        };

        return pick;
    }
}
